import type { LatLng, CoordinateSet, MapBounds } from "@/map/types/map";

/**
 * Calculate bounds from all coordinate points
 */
export const calculateMapBounds = (coordinates: CoordinateSet[]): google.maps.LatLngBounds | null => {
  if (coordinates.length === 0) return null;

  const allPoints = coordinates.flatMap((coord) => coord.path);
  if (allPoints.length === 0) return null;

  const bounds = new google.maps.LatLngBounds();
  allPoints.forEach((point) => {
    bounds.extend({ lat: point.lat, lng: point.lng });
  });

  return bounds;
};

/**
 * Calculate center point from coordinates
 */
export const calculateMapCenter = (coordinates: CoordinateSet[]): { lat: number; lng: number } => {
  if (coordinates.length === 0) return { lat: 0, lng: 0 };

  // If there's only one coordinate set with one point, use that as center
  if (coordinates.length === 1 && coordinates[0].path.length === 1) {
    return coordinates[0].path[0];
  }

  // For multiple coordinates or multiple points, calculate center from bounds
  const allPoints = coordinates.flatMap((coord) => coord.path);
  if (allPoints.length === 0) return { lat: 0, lng: 0 };

  const bounds = allPoints.reduce(
    (acc, point) => {
      return {
        minLat: Math.min(acc.minLat, point.lat),
        maxLat: Math.max(acc.maxLat, point.lat),
        minLng: Math.min(acc.minLng, point.lng),
        maxLng: Math.max(acc.maxLng, point.lng),
      };
    },
    {
      minLat: allPoints[0].lat,
      maxLat: allPoints[0].lat,
      minLng: allPoints[0].lng,
      maxLng: allPoints[0].lng,
    }
  );

  return {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  };
};

/**
 * Check if map should show empty state
 */
export const shouldShowEmptyState = (
  coordinates: CoordinateSet[],
  isEmptyOverride?: boolean
): boolean => {
  if (isEmptyOverride !== undefined) return isEmptyOverride;
  return coordinates.length === 0 || coordinates.every((coord) => coord.path.length === 0);
};

/**
 * Convert lat/lng to screen coordinates
 */
export const latLngToScreenPoint = (
  latLng: google.maps.LatLng,
  map: google.maps.Map
): { x: number; y: number } | null => {
  const projection = map.getProjection();
  if (!projection) return null;

  const point = projection.fromLatLngToPoint(latLng);
  if (!point) return null;

  const zoom = map.getZoom();
  if (!zoom) return null;

  const scale = Math.pow(2, zoom);
  return {
    x: point.x * scale,
    y: point.y * scale,
  };
};
